import React, { useState } from 'react';
import {
  ButtonToolbar,
  Col, Form,
  OverlayTrigger, Row, Tooltip,
} from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { faImage, faPaperclip, faTrash } from '@fortawesome/free-solid-svg-icons';
import meta from '../../../../meta';
import Flex from '../../common/Flex';
import IconButton from '../../common/IconButton';
import ComposeAttachment from './ComposeAttachment';
import useEditor from '../../../../containers/newEditor/hook/editor';
import { EditorControls, editorHooks } from '../../../../containers/basicEditor/editorControls';
import EditorContainer from '../../../../containers/newEditor/editorContainer';
import { SaveButton } from '../../../bootStrap/buttons';

function Compose() {
  const { id } = useParams();
  const navigate = useNavigate();
  const onSave = (item) => {
    if (item && item.id) {
      navigate(`/email/compose/${item.id}`);
    }
  };
  const {
    data, actions, fields, nonFieldErrors, options, isNew, changed,
    loading, systemErrors, fieldErrors,
  } = useEditor({
    backendURL: meta.app.emailAdmin.backendURL,
    id,
    onSaveCallBack: onSave,
  });

  const [attachments, setAttachments] = useState([]);

  const handleAddAttachment = (files) => {
    if (files.length === 0) return;
    const fileArray = [];
    Array.from(files).forEach((file) => {
      const { name, size, type } = file;

      const newFile = {
        id: name + 1 + Date.now(),
        name,
        size,
        type,
      };
      fileArray.push(newFile);
    });
    setAttachments([...attachments, ...fileArray]);
  };

  const handleDetachAttachment = () => {
    setAttachments(attachments.filter((attachment) => attachment.id !== id));
  };

  const userProps = editorHooks.useItemInputProps('user', data, fields, fieldErrors, actions.onChange);
  const titleProps = editorHooks.useStringInputProps('title', data, fields, fieldErrors, actions.onChange);
  const inspectionProps = editorHooks.useItemInputProps('inspection', data, fields, fieldErrors, actions.onChange);
  const lifeTimeProps = editorHooks.useDateInputProps('life_time', data, fields, fieldErrors, actions.onChange);
  const messageTypeProps = editorHooks.useSelectorInputProps('message_type', data, fields, fieldErrors, actions.onChange);
  const descriptionProps = editorHooks.useTextInputProps('description', data, fields, fieldErrors, actions.onChange);

  return (
    <EditorContainer
      isNew={isNew}
      name={options.name}
      repr={data.title}
      isLoading={loading}
      err={systemErrors}
      nonFieldErrors={nonFieldErrors}
      onClearNonFieldErrors={actions.onClearNonFieldErrors}
      onClearErrors={actions.onClearErrs}
      onClose={actions.onClose}
      changed={changed}
    >
      <Row>
        <div className="border border-top-0 border-200">
          <Col>
            <EditorControls.ItemPicker {...userProps} />
          </Col>
          <Col>
            <EditorControls.ItemPicker {...inspectionProps} />
          </Col>
          <Col>
            <EditorControls.SelectorInput {...messageTypeProps} />
          </Col>
          <Col>
            <EditorControls.DateInput {...lifeTimeProps} />
          </Col>
        </div>
      </Row>
      <Row>
        <div className="border border-y-0 border-200">
          <Col>
            <EditorControls.StringInput {...titleProps} />
          </Col>
        </div>
      </Row>
      <Row>
        <div className="border border-0 border-200">
          <Col>
            <EditorControls.TextInput {...descriptionProps} />
          </Col>
        </div>
      </Row>
      <Row>
        {attachments.length > 0 && (
          <div className="bg-light px-x1 py-3">
            <Flex direction="column" inline>
              {attachments.map((attachment) => (
                <ComposeAttachment
                  {...attachment}
                  key={attachment.id}
                  handleDetachAttachment={handleDetachAttachment}
                />
              ))}
            </Flex>
          </div>
        )}
      </Row>
      { attachments.length > 0 && (
      <Row>
        <ButtonToolbar className="gap-2 px-3 py-2 border rounded">
          <Form.Group controlId="email-attachment">
            <Form.Control
              type="file"
              multiple
              className="d-none"
              onChange={({ target }) => handleAddAttachment(target.files)}
            />
            <OverlayTrigger
              overlay={(
                <Tooltip style={{ position: 'fixed' }} id="previousTooltip">
                  Прикріпити файл
                </Tooltip>
              )}
            >
              <div>
                <IconButton
                  as={Form.Label}
                  className="me-2 mb-0"
                  variant="light"
                  size="sm"
                  icon={faPaperclip}
                  iconClassName="fs-1"
                  transform="down-2"
                />
              </div>
            </OverlayTrigger>
          </Form.Group>
          <Form.Group controlId="email-image">
            <Form.Control
              type="file"
              className="d-none"
              accept="image/*"
              multiple
              onChange={({ target }) => handleAddAttachment(target.files)}
            />
            <OverlayTrigger
              overlay={(
                <Tooltip style={{ position: 'fixed' }} id="previousTooltip">
                  Прикріпити картинку
                </Tooltip>
              )}
            >
              <div>
                <IconButton
                  as={Form.Label}
                  className="me-2 mb-0"
                  variant="light"
                  size="sm"
                  icon={faImage}
                  iconClassName="fs-1"
                  transform="down-2"
                />
              </div>
            </OverlayTrigger>
          </Form.Group>
          <OverlayTrigger
            overlay={(
              <Tooltip style={{ position: 'fixed' }} id="previousTooltip">
                Видалити
              </Tooltip>
            )}
          >
            <div>
              <IconButton
                className="ms-2 mb-0"
                variant="light"
                size="sm"
                icon={faTrash}
                onClick={() => {
                  setAttachments([]);
                }}
              />
            </div>
          </OverlayTrigger>
        </ButtonToolbar>
      </Row>
      )}
      <Row>
        <ButtonToolbar className="gap-2 px-3 py-2 border rounded">
          <SaveButton
            onClick={actions.onSaveWithoutExit}
            disabled={false}
          />
        </ButtonToolbar>
      </Row>
    </EditorContainer>
  );
}

Compose.propTypes = {
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};

Compose.defaultProps = {
  onClose: null,
};

export default Compose;
